import { Badge } from "@loot-os/ui/components/badge";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { Fragment } from "react";

import { formatDate, formatNumber } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { ErrorAlert } from "./ErrorAlert";

/**
 * Who has this box template switched on — the tenant-facing half of a box
 * box, which the drop-table editor cannot show.
 *
 * A row is an environment, not a tenant: enablement is per environment, so a tenant
 * with the box live in Production and off in a sandbox is two different
 * answers. The tenant cell is written once per group so the eye reads tenants first
 * and environments second.
 *
 * Each environment has its switch here too (`platform.boxTemplates.setEnablement`, audited), so
 * staff can roll a template out or pull it without switching into every tenant; it is the same
 * switch the tenant has on their own Boxes page, which the tenant name links to. Only an active
 * template can be switched on; switching off always works.
 *
 * Rendered as the Tenants tab of the box template editor, which is why it
 * carries no heading of its own and fetches on mount: opening the tab is the
 * request.
 */
export function BoxTemplateEnablements({ boxId, active }: { boxId: string; active: boolean }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const tenants = useQuery(trpc.platform.boxTemplates.enablements.queryOptions({ boxId }));
  const setEnablement = useMutation(
    trpc.platform.boxTemplates.setEnablement.mutationOptions({
      onSuccess: (result) => {
        // The rows here, and the template's enabled count in the editor's header notice.
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.boxTemplates.enablements.queryKey({ boxId }),
        });
        void queryClient.invalidateQueries({ queryKey: trpc.platform.boxTemplates.get.pathKey() });
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.boxTemplates.list.pathKey(),
        });
        toast.success(result.enabled ? "Enabled" : "Disabled");
      },
    }),
  );
  const switching = setEnablement.isPending ? setEnablement.variables?.environmentId : undefined;

  const rows = tenants.data ?? [];
  const environmentCount = rows.reduce((sum, tenant) => sum + tenant.environments.length, 0);
  const enabledCount = rows.reduce((sum, tenant) => sum + tenant.enabledCount, 0);
  const enabledTenants = rows.filter((tenant) => tenant.enabledCount > 0).length;

  return (
    <div className="flex flex-col gap-3">
      {/* No heading: the tab that renders this is the heading. */}
      <p className="text-sm text-muted-foreground">
        {tenants.isLoading
          ? "Loading enablements…"
          : rows.length === 0
            ? "No tenants on the platform yet."
            : `On in ${formatNumber(enabledCount)} of ${formatNumber(environmentCount)} ` +
              `${environmentCount === 1 ? "environment" : "environments"}, across ` +
              `${formatNumber(enabledTenants)} of ${formatNumber(rows.length)} ` +
              `${rows.length === 1 ? "tenant" : "tenants"}. Switch it per environment here, or ` +
              "the tenant does on their own Boxes page." +
              (active ? "" : " Only an active template can be switched on.")}
      </p>

      {tenants.error && <ErrorAlert message={tenants.error.message} />}

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Environment</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>Since</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                    {tenants.isLoading ? "Loading…" : "No tenants yet."}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((tenant) => (
                <Fragment key={tenant.id}>
                  {tenant.environments.length === 0 && (
                    <TableRow>
                      <TableCell>
                        <TenantCell id={tenant.id} name={tenant.name} slug={tenant.slug} />
                      </TableCell>
                      <TableCell colSpan={3} className="text-muted-foreground">
                        No environments
                      </TableCell>
                    </TableRow>
                  )}
                  {tenant.environments.map((env, index) => (
                    <TableRow key={env.id}>
                      {/* Written once per tenant; the blank cells below it are the
                          grouping, so nothing repeats down the column. */}
                      <TableCell className="align-top">
                        {index === 0 ? (
                          <TenantCell id={tenant.id} name={tenant.name} slug={tenant.slug} />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          <span>{env.name}</span>
                          {env.type === "live" && <Badge variant="secondary">live</Badge>}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Switch
                          aria-label={`${tenant.name} ${env.name}`}
                          checked={env.enabled}
                          // Off always works; on needs an active template.
                          disabled={switching === env.id || (!env.enabled && !active)}
                          title={
                            !env.enabled && !active
                              ? "Only an active template can be switched on"
                              : undefined
                          }
                          onCheckedChange={(enabled) =>
                            setEnablement.mutate({ boxId, environmentId: env.id, enabled })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {env.enabledAt ? formatDate(env.enabledAt) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/** Links into the tenant's Boxes page, where the tenant's own switch is. */
function TenantCell({ id, name, slug }: { id: string; name: string; slug: string }) {
  return (
    <Link
      to="/tenants/$tenantId/boxes"
      params={{ tenantId: id }}
      className="inline-flex items-center gap-2 font-medium hover:underline"
      title={`Open ${name}'s boxes`}
    >
      <Building2 className="size-4 shrink-0" />
      {name}
      <span className="font-mono text-xs font-normal text-muted-foreground">{slug}</span>
    </Link>
  );
}
